// The model behind `/watch-cpu` — two machines playing, and a record of why.
//
// **Pure, and separated from the page for the reason `celebrations.ts` and
// `defeat.ts` are**: what is worth testing here is which turns become entries,
// what a trade is worth and where a scrub lands, none of which needs a browser,
// a renderer or a worker. The page is timing, layout and a canvas.
//
// The one architectural fact everything rests on: **the command log is the save
// format**, so a move list is a view of the log and jumping to a move is
// replaying a prefix of it. That is `GameStore.undo` generalised — except that
// scrubbing must *not* truncate, because the game is still being played
// underneath. Truncation is a separate, deliberate act (`forkAt`).

import {
  BISHOP,
  KING,
  KNIGHT,
  NORTH,
  paddedCells,
  PAWN,
  pieceColor,
  pieceType,
  QUEEN,
  ROOK,
  SOUTH,
  square,
  type Direction,
  type Piece,
  type PieceType,
  type Side
} from "src/engine/rules/board";
import { pieceAt } from "src/engine/rules/position";
import { applyCommand, type Command, type GameState } from "src/engine/rules/commands";
import { PIECE_NAMES, SURRENDER_VALUE } from "src/engine/rules/rules";
import { streamFor } from "src/engine/rules/random";
import { ARMIES } from "src/engine/ai/scenarios";
import { squareLabel } from "src/engine/session/gridLabels";

/**
 * One entry in the move list.
 *
 * **A turn, not a command.** A planned turn is routinely six or seven commands
 * — lanes drawn, a policy set, a part re-aimed — and a list with `set-dump` in
 * it is not a move list. So an entry is the turn-spending command, and the
 * bookkeeping around it is reported as a count rather than as entries.
 */
export interface MoveRecord {
  /**
   * Index in the log of the command this entry is about.
   *
   * **This is the identity of the entry and the seek target at once.** The free
   * half of a turn is dispatched *before* its move (`[...free, best.command]`),
   * so replaying `log.slice(0, at + 1)` lands on the board as it stood when
   * that turn finished — the lanes it drew included.
   */
  at: number;
  /** 1-based, counting only entries in this list — what a reader calls "move 12". */
  number: number;
  side: number;
  /** `fullmove` at the time, which is what a chess player means by "turn". */
  turn: number;
  kind: Command["type"];
  /** "Knight f3→e5", "Rook forges a Bishop" — already a sentence. */
  label: string;
  /** What died, if anything. Priced by `SURRENDER_VALUE`. */
  captured?: { type: number; side: number; value: number };
  /** Which stance proposed it; absent when nothing was judged. See `PlanReport`. */
  stance?: string;
  /** The judge's score for the board afterwards, in centipawns. */
  value?: number;
  /** True when the worker missed its deadline and this is the simple move. */
  fallback: boolean;
  /** Commands this turn spent that were not the move itself. */
  free: number;
}

/** A running read of the game, for the sidebar. */
export interface WatchTally {
  moves: number;
  turns: number;
  /** Material taken, per side, by `SURRENDER_VALUE`. */
  taken: number[];
  /** How many pieces each side has lost. */
  losses: number[];
  /** How often each stance won the judge, most-used first. */
  stances: { name: string; count: number }[];
  /** Turns the planner failed on and fell back. */
  fallbacks: number;
}

/**
 * Which commands are a *turn* rather than bookkeeping.
 *
 * Read off the rules' own list of what spends the clock (PLAN: a move, a
 * knight's re-aim, a forge) rather than by excluding the things that do not —
 * an exclusion list is one somebody has to remember to extend, and the failure
 * is a `set-handoff` appearing in the move list, which reads as the game
 * playing itself twice.
 */
const SPENDS_TURN = new Set<Command["type"]>([
  "move",
  "forge",
  "re-aim",
  "build",
  "demolish",
  "sacrifice",
  "pass"
]);

export const spendsTurn = (command: Command): boolean => SPENDS_TURN.has(command.type);

/**
 * What a command did, given the board on either side of it.
 *
 * **Both states, because neither alone can answer.** The victim of a capture is
 * gone from the board afterwards and the mover is gone from its origin, so a
 * label needs `before`; whether anything actually died is a fact about the
 * difference. Answers null for a command that did not spend the turn.
 */
export function recordOf(
  before: GameState,
  after: GameState,
  command: Command,
  at: number,
  number: number,
  report?: { stance?: string; value?: number; fallback?: boolean; free?: number }
): MoveRecord | null {
  if (!spendsTurn(command)) return null;

  const side = before.position.turn;
  const record: MoveRecord = {
    at,
    number,
    side,
    turn: before.position.fullmove,
    kind: command.type,
    label: labelFor(before, command),
    stance: report?.stance,
    value: report?.value,
    fallback: report?.fallback ?? false,
    free: report?.free ?? 0
  };

  const victim = victimOf(before, after, command);
  if (victim) record.captured = victim;
  return record;
}

/**
 * What died on this command, if anything.
 *
 * **The count says whether, the board says what.** Reading `pieceAt(before, to)`
 * alone misses an en passant, whose victim never stands on the square the mover
 * lands on — and diffing `counts` alone knows a piece was lost and not which
 * rank it was. Together they cover both: the count is the test, the board is
 * the detail, and a capture the board cannot explain is still reported with the
 * side that lost it.
 */
function victimOf(
  before: GameState,
  after: GameState,
  command: Command
): MoveRecord["captured"] {
  const sides = before.position.sides;
  for (let side = 0; side < sides; side += 1) {
    if (side === before.position.turn) continue;
    const lost = (before.position.counts[side] ?? 0) - (after.position.counts[side] ?? 0);
    if (lost <= 0) continue;
    // The ordinary capture: the victim was standing on the destination.
    let type = 0;
    if (command.type === "move") {
      const piece = pieceAt(before.position, command.to);
      if (piece && pieceColor(piece) === side) type = pieceType(piece);
    }
    return { type, side, value: SURRENDER_VALUE[type as keyof typeof SURRENDER_VALUE] ?? 0 };
  }
  return undefined;
}

/** "Knight f3→e5", "Rook forges", "King raises" — a sentence, not a command dump. */
function labelFor(before: GameState, command: Command): string {
  if (command.type === "move") {
    const piece: Piece = pieceAt(before.position, command.from);
    const name = piece ? (PIECE_NAMES[pieceType(piece)] ?? "Piece") : "Piece";
    return `${name} ${squareLabel(command.from)}→${squareLabel(command.to)}`;
  }
  if (command.type === "forge") {
    // The foundry when one was named, else where the piece lands — a forge
    // aimed at a square alone has no one machine to name (PLAN-UI §28.4).
    const where = command.square ?? command.at;
    return where === undefined ? "Forge" : `Forge at ${squareLabel(where)}`;
  }
  // A re-aim is named by the *pawn* rather than the knight spending the turn:
  // the knight is the cost and the pawn is what changed, which is what a reader
  // is looking for on the board.
  if (command.type === "re-aim") return `Re-aim ${squareLabel(command.pawn)}`;
  if (command.type === "build") return `Build at ${squareLabel(command.square)}`;
  if (command.type === "demolish") return `Demolish at ${squareLabel(command.square)}`;
  if (command.type === "sacrifice") return `Sacrifice at ${squareLabel(command.square)}`;
  if (command.type === "pass") return "End turn";
  return command.type;
}

/**
 * The sidebar's numbers, added up from the entries.
 *
 * Derived rather than accumulated, so it cannot drift from the list beside it —
 * the same argument `totalScore` makes about reading `runTally + tally` instead
 * of keeping a second counter.
 */
export function tallyOf(records: readonly MoveRecord[], sides = 2): WatchTally {
  const taken = new Array<number>(sides).fill(0);
  const losses = new Array<number>(sides).fill(0);
  const stances = new Map<string, number>();
  let fallbacks = 0;
  let turns = 0;

  for (const record of records) {
    turns = Math.max(turns, record.turn);
    if (record.fallback) fallbacks += 1;
    if (record.stance) stances.set(record.stance, (stances.get(record.stance) ?? 0) + 1);
    const hit = record.captured;
    if (hit) {
      if (record.side < sides) taken[record.side] += hit.value;
      if (hit.side < sides) losses[hit.side] += 1;
    }
  }

  return {
    moves: records.length,
    turns,
    taken,
    losses,
    // Ties by name, so the order is stable between renders rather than
    // depending on which stance happened to reach a count first.
    stances: [...stances.entries()]
      .map(([name, count]) => ({ name, count }))
      .sort((a, b) => b.count - a.count || a.name.localeCompare(b.name)),
    fallbacks
  };
}

/**
 * The board as it stood after log entry `upto`.
 *
 * **Replay, never an inverse.** The reason is `GameStore.undo`'s: un-applying
 * would be a second implementation of every rule, one that has to agree with
 * `applyCommand` about ticks, cooking queues and loot rolls forever. Replaying
 * is slower and cannot be wrong — and this is driven by a person clicking a
 * move, so it is paid once per click rather than per frame.
 *
 * `upto` of -1 is the opening board, which is a real answer: it is the game
 * before anybody moved.
 */
export function stateAt(base: GameState, log: readonly Command[], upto: number): GameState {
  let state = base;
  for (let i = 0; i <= upto && i < log.length; i += 1) state = applyCommand(state, log[i]);
  return state;
}

/**
 * The log truncated to a chosen move, for replaying the game from there.
 *
 * **A fork, and destructive on purpose** — everything after the chosen move is
 * discarded, because the alternative is two futures and no way to say which one
 * the board is showing. That is the same trade `undo` makes and the reason the
 * control saying so is worded as replacing the rest rather than as "rewind".
 */
export function forkAt(log: readonly Command[], upto: number): Command[] {
  return log.slice(0, Math.max(0, upto + 1));
}

/** How far past the classic eight the watched board runs, on every side. */
export const WATCH_PAD = 3;
/** Ore piles per side, and refined ingots per side. Doubled by the symmetry. */
export const WATCH_ORE = 9;
export const WATCH_INGOTS = 5;

/** The board two machines meet on, as the opening command takes it. */
export interface FairBoard {
  cells: number[];
  armies: [square: number, side: Side, type: PieceType, facing: Direction][];
  heaps: [number, number][];
  metal: [number, number, number][];
  /** The side of the square board, in cells. */
  size: number;
  /** `min + max` of the coordinate range — what `rotated` turns about. */
  span: number;
}

/** Two armies and the board they meet on. */
export interface Matchup {
  seed: number;
  /** A `kingKind` per side, as `new-game.kingKinds` takes it. */
  kings: number[];
  /** What to call each army in the sidebar. */
  names: string[];
  board: FairBoard;
}

/**
 * The half-turn partner of a square, given the sum of the board's extremes.
 *
 * **Rotation, not reflection, and the difference is what makes it fair rather
 * than merely tidy.** Reflecting across the middle rank is what the chess
 * opening does, and it leaves both kings on the same file — so the two armies
 * face mirrored, not identical, problems the moment anything is placed off
 * centre. A half turn maps the whole board onto itself including its ore, so
 * whatever one side is looking at, the other is looking at the same thing from
 * the other end.
 *
 * **`span` is `min + max`, never the size.** A padded board does not start at
 * nought — `paddedCells(3)` runs −3..10 — so an index-based rotation puts ore
 * off the board entirely. Driven: every heap landed outside `cells` and the
 * reducer silently skipped it, which is a fair board with no ore on it.
 *
 * `span` is odd for every padded board (min + max = 7 at any pad), so **no
 * square is its own partner** — there is no centre cell to double-count, which
 * is what lets ore be placed by halves with no dedupe.
 */
export const rotated = (file: number, rank: number, span: number): [number, number] => [
  span - file,
  span - rank
];

/** The classic back rank, in file order. */
const BACK_RANK: PieceType[] = [ROOK, KNIGHT, BISHOP, QUEEN, KING, BISHOP, KNIGHT, ROOK];

/**
 * A board that is the same for both armies, with something on it to fight over.
 *
 * **Generated rather than chosen from `SCENARIOS`.** Every authored board is
 * authored for a reason — to show a mechanic, to be a tutorial, to be a
 * particular fight — and none of them is *fair* in the sense a match between
 * two machines needs: the first game this page played was decided by
 * insufficient material at turn 16 on a board that had nothing on it to
 * mine. Here the only asymmetry left is which army each side is playing, which
 * is the thing being watched.
 *
 * The board is square and padded, so **every side has a promotion wall**
 * (`promotionWall` grants one on any square, whole board bigger than the
 * classic eight) — the same reason the Factory Map pads.
 */
export function fairBoard(seed: number): FairBoard {
  // **A padded board does not start at nought.** `paddedCells(3)` spans −3..10,
  // so every coordinate here is written in that range and the rotation is about
  // `MIN + MAX` rather than about an index.
  const min = -WATCH_PAD;
  const max = 7 + WATCH_PAD;
  const span = min + max;
  const size = max - min + 1;
  const cells = paddedCells(WATCH_PAD);
  const armies: FairBoard["armies"] = [];
  const taken = new Set<number>();

  // White's half, then the same thing rotated. Written once, so the two armies
  // cannot drift apart — a second literal for Black is how "fair" stops being
  // true after the first edit.
  const place = (file: number, rank: number, type: PieceType) => {
    const mine = square(file, rank);
    const [rf, rr] = rotated(file, rank, span);
    const theirs = square(rf, rr);
    // Facing is rotated with the piece: a half turn takes north to south, which
    // is exactly each side's own forward.
    armies.push([mine, 0, type, NORTH]);
    armies.push([theirs, 1, type, SOUTH]);
    taken.add(mine);
    taken.add(theirs);
  };

  // On the classic squares, so the padding is open ground on every side rather
  // than a shoulder one army is standing on.
  for (let i = 0; i < BACK_RANK.length; i += 1) {
    place(i, 0, BACK_RANK[i]);
    place(i, 1, PAWN);
  }

  // Ore and ingots go in one half and are mirrored into the other. Drawn from
  // the page's own stream so a seed is a board — the same reason the matchup is.
  const roll = streamFor(seed, 1, WATCH_STREAM);
  const heaps: [number, number][] = [];
  const metal: [number, number, number][] = [];
  // The lower half by rank, which the rotation maps onto the upper half exactly.
  const highest = Math.floor(span / 2);

  const free = (file: number, rank: number): boolean => {
    const sq = square(file, rank);
    const [rf, rr] = rotated(file, rank, span);
    return !taken.has(sq) && !taken.has(square(rf, rr));
  };

  const scatter = (count: number, put: (file: number, rank: number) => void) => {
    // Bounded tries rather than a while: a full half would otherwise spin, and
    // a board with slightly less ore than asked for is a better failure than a
    // page that never opens.
    for (let placed = 0, tries = 0; placed < count && tries < count * 40; tries += 1) {
      const file = min + (Math.floor(roll() * size) % size);
      const rank = min + (Math.floor(roll() * (highest - min + 1)) % (highest - min + 1));
      if (!free(file, rank)) continue;
      put(file, rank);
      taken.add(square(file, rank));
      const [rf, rr] = rotated(file, rank, span);
      taken.add(square(rf, rr));
      placed += 1;
    }
  };

  scatter(WATCH_ORE, (file, rank) => {
    const amount = 2 + Math.floor(roll() * 4);
    const [rf, rr] = rotated(file, rank, span);
    heaps.push([square(file, rank), amount]);
    heaps.push([square(rf, rr), amount]);
  });

  scatter(WATCH_INGOTS, (file, rank) => {
    // Grade 1 or 2 — something already cooked, so an army has a reason to reach
    // for a forge rather than only for a pit.
    const grade = 1 + Math.floor(roll() * 2);
    const count = 1 + Math.floor(roll() * 2);
    const [rf, rr] = rotated(file, rank, span);
    metal.push([square(file, rank), grade, count]);
    metal.push([square(rf, rr), grade, count]);
  });

  return { cells, armies, heaps, metal, size, span };
}

/**
 * A randomised but coherent pairing, reproducible from the seed.
 *
 * **Coherent means the armies are chosen and then left to play themselves.**
 * The factions come off `ARMIES` and no profile is named, so each side takes
 * its evaluation from its own king. Since §31 R2 that table holds the factory
 * army alone, so the pairing is a mirror.
 *
 * Seeded through `streamFor` rather than `Math.random` so a shared link is the
 * same game: the seed rides in the opening command and replays the play, and
 * this makes it replay the *setup* too.
 */
export function matchupFor(seed: number): Matchup {
  // Two draws off one stream, in a fixed order. The *board* takes its own
  // stream (ply 1) so that changing how much ore is scattered cannot re-roll
  // which armies play — the separation `styleForStage` keeps for the same
  // reason.
  const roll = streamFor(seed, 0, WATCH_STREAM);
  // `ARMIES` is a readonly *tuple*, so a filtered copy is an array and not that
  // type — the element type is what this actually wants.
  type Army = (typeof ARMIES)[number];
  const pick = (from: readonly Army[]): Army =>
    from[Math.floor(roll() * from.length) % from.length];
  const white = pick(ARMIES);
  // **The second army is drawn from what is left**, so a duel is never an army
  // against itself. A mirror is a legitimate game and a poor *watch*: the whole
  // question this page is for is what two different evaluations do to each
  // other, and a sidebar reading "Classic vs Classic" cannot say which column
  // is which. Still exactly one draw either way, so no seed is re-rolled by
  // this — the rule `rollLoot` keeps about always taking the same number.
  const rest = ARMIES.filter((army) => army.id !== white.id);
  const black = pick(rest.length > 0 ? rest : ARMIES);
  return {
    seed,
    kings: [white.kind, black.kind],
    names: [white.name, black.name],
    board: fairBoard(seed)
  };
}

/**
 * The opening command for a matchup.
 *
 * An **empty FEN** with everything placed through `armies`, because a FEN is
 * two-sided *and* reflectional: it can only say "the classic opening", which is
 * the one board shape this page is deliberately not using. `placeArmies` is
 * already the door for pieces a FEN cannot describe.
 */
export function openingFor(matchup: Matchup): Extract<Command, { type: "new-game" }> {
  return {
    type: "new-game",
    fen: EMPTY_FEN,
    seed: matchup.seed,
    cells: matchup.board.cells,
    armies: matchup.board.armies,
    heaps: matchup.board.heaps,
    metal: matchup.board.metal,
    kingKinds: matchup.kings
  };
}

/**
 * A board with no pieces on it, for `armies` to fill.
 *
 * The castling field is `-` on purpose: rights are read off the FEN, and a
 * board whose kings and rooks were placed by `armies` never had any.
 */
const EMPTY_FEN = "8/8/8/8/8/8/8/8 w - - 0 1";

/**
 * What happened, in a sentence, or null while the game is still on.
 *
 * **A watched game that has ended looks exactly like one that has broken**, and
 * that is not a hypothetical — the first game this page played stopped at turn
 * 16 and read as a stall until the status was checked by hand. A spectator has
 * no other signal: there is no clock to run out, no control that stops working,
 * and the board simply holds still.
 *
 * The mated side is **the one on the clock**, so the winner is the other army —
 * which is the only place this needs to know that a duel has two sides.
 */
export function outcomeOf(
  status: string,
  turn: number,
  names: readonly string[]
): { over: boolean; text: string } | null {
  if (status === "checkmate") {
    // Whoever is to move has no legal move and is in check, so they lost.
    return { over: true, text: `Checkmate — ${names[turn === 0 ? 1 : 0] ?? "the other army"} wins` };
  }
  if (status === "stalemate") {
    return { over: true, text: `Stalemate — ${names[turn] ?? "an army"} has no legal move` };
  }
  if (status === "draw-fifty-move") return { over: true, text: "Draw — fifty moves without progress" };
  if (status === "draw-repetition") return { over: true, text: "Draw — the position repeated three times" };
  if (status === "draw-material") return { over: true, text: "Draw — neither army can mate" };
  // Check is not an ending, but it is the one in-progress state worth saying
  // out loud: it explains a turn that looks like a blunder.
  if (status === "check") return { over: false, text: `${names[turn] ?? "An army"} is in check` };
  return null;
}

/**
 * This page's own stream salt.
 *
 * Its own number for the reason `styleForStage` has one: drawing off a stream
 * something else already uses would change what that thing rolls as a side
 * effect, and the two have nothing to do with each other.
 */
export const WATCH_STREAM = 71;
